import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { resolveApiMessage, showToast } from '../../../helpers';
import { NAVIGATE_PATH as PATH } from '../../../helpers/constants';
import {
  createOpsAssignment,
  getOpsClasses,
  getOpsInstructors,
  removeOpsAssignment,
} from '../../../requests';
import {
  getAccountInstructors,
  getOpsAssignmentsFiltered,
} from '../../../requests/opsReports';
import AccountPicker, { TPickedAccount } from '../OpsReports/AccountPicker';
import {
  btnDanger,
  btnPrimary,
  btnSecondary,
  Card,
  EmptyState,
  inputClass,
  labelClass,
  OriginBadge,
  PageBody,
  PageHeader,
  Pagination,
  Pill,
  rowClickClass,
  tableClass,
  tdClass,
  thClass,
  theadClass,
  Toolbar,
  SortTh,
  useListSort,
} from '../ui';

const OpsAssignments: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [instructor, setInstructor] = useState('');
  // The account filter arrives on the URL from the reports screen; the
  // picker only needs a name once it is chosen, so resolve it lazily.
  const accountIdParam = searchParams.get('accountId') ?? '';
  const [account, setAccount] = useState<TPickedAccount | null>(
    accountIdParam ? { id: accountIdParam, name: accountIdParam } : null,
  );
  const accountId = account?.id ?? '';
  useQuery({
    queryKey: ['opsAccountInstructors', accountId],
    queryFn: async () => {
      const r = await getAccountInstructors(accountId);
      if (r.accountName) setAccount({ id: accountId, name: r.accountName });
      return r;
    },
    enabled: !!account && account.name === account.id,
  });
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [upcoming, setUpcoming] = useState(true);
  const [page, setPage] = useState(1);
  const { sortKey, sortDir, toggleSort } = useListSort();
  const [assignOpen, setAssignOpen] = useState(false);

  // Assign form state
  const [classQuery, setClassQuery] = useState('');
  const [classId, setClassId] = useState('');
  const [classLabel, setClassLabel] = useState('');
  const [pickInstructor, setPickInstructor] = useState('');
  const [classRate, setClassRate] = useState('');
  const [prepRate, setPrepRate] = useState('');
  const [contractType, setContractType] = useState('');

  useEffect(() => {
    const t = setTimeout(() => {
      setQ(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: [
      'opsAssignments',
      instructor,
      accountId,
      q,
      upcoming,
      sortKey,
      sortDir,
      page,
    ],
    queryFn: () =>
      getOpsAssignmentsFiltered({
        sort: sortKey || undefined,
        dir: sortKey ? sortDir : undefined,
        instructor: instructor || undefined,
        accountId: accountId || undefined,
        q: q || undefined,
        upcoming,
        page,
      }),
  });

  const { data: instructors = [] } = useQuery({
    queryKey: ['opsInstructors'],
    queryFn: getOpsInstructors,
  });

  const { data: classHits } = useQuery({
    queryKey: ['opsAssignClassSearch', classQuery],
    queryFn: () => getOpsClasses({ q: classQuery, page: 1 }),
    enabled: assignOpen && classQuery.trim().length >= 2 && !classId,
  });

  // Prefill rates from the picked instructor's latest assignment
  useEffect(() => {
    const meta = instructors.find((i) => i.name === pickInstructor);
    if (meta?.latestRates) {
      setClassRate(
        meta.latestRates.classRate != null
          ? String(meta.latestRates.classRate)
          : '',
      );
      setPrepRate(
        meta.latestRates.prepRate != null
          ? String(meta.latestRates.prepRate)
          : '',
      );
      setContractType(meta.latestRates.contractType ?? '');
    }
  }, [pickInstructor, instructors]);

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['opsAssignments'] });

  const create = useMutation({
    mutationFn: createOpsAssignment,
    onSuccess: () => {
      showToast('Instructor assigned', 'success');
      setAssignOpen(false);
      setClassId('');
      setClassLabel('');
      setClassQuery('');
      setPickInstructor('');
      refresh();
    },
    onError: (err) => showToast(resolveApiMessage(err), 'error'),
  });

  const remove = useMutation({
    mutationFn: removeOpsAssignment,
    onSuccess: () => {
      showToast('Assignment removed', 'success');
      refresh();
    },
    onError: (err) => showToast(resolveApiMessage(err), 'error'),
  });

  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <>
      <PageHeader
        title="Instructor assignments"
        meta={data && `${data.total.toLocaleString()} assignments`}
        actions={
          <button
            className={assignOpen ? btnSecondary : btnPrimary}
            onClick={() => setAssignOpen((o) => !o)}
          >
            {assignOpen ? 'Close' : 'Assign instructor'}
          </button>
        }
      />
      <Toolbar>
        <select
          className={inputClass}
          value={instructor}
          onChange={(e) => {
            setInstructor(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All instructors</option>
          {instructors.map((i) => (
            <option key={i.name}>{i.name}</option>
          ))}
        </select>
        <input
          className={`${inputClass} w-56`}
          placeholder="Search course name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <AccountPicker
          value={account}
          onChange={(a) => {
            setAccount(a);
            setPage(1);
            if (a) setSearchParams({ accountId: a.id });
            else setSearchParams({});
          }}
          placeholder="Filter by account…"
          className="w-64"
        />
        <label className="flex items-center gap-1.5 text-sm text-gray-three">
          <input
            type="checkbox"
            className="ops-check"
            checked={upcoming}
            onChange={(e) => {
              setUpcoming(e.target.checked);
              setPage(1);
            }}
          />
          Upcoming only
        </label>
      </Toolbar>

      {assignOpen && (
        <div className="px-8 pb-4">
          <Card title="Assign an instructor">
            <div className="grid grid-cols-1 gap-4 p-5 md:grid-cols-2">
              <div>
                <div className={labelClass}>Class</div>
                {classId ? (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="rounded-md bg-blue-100 px-2.5 py-1 font-medium text-blue-primary">
                      {classLabel}
                    </span>
                    <button
                      type="button"
                      className="text-xs font-medium text-gray-bold underline hover:text-blue-primary"
                      onClick={() => {
                        setClassId('');
                        setClassLabel('');
                        setClassQuery('');
                      }}
                    >
                      change
                    </button>
                  </div>
                ) : (
                  <>
                    <input
                      className={`${inputClass} w-full`}
                      placeholder="Search classes…"
                      value={classQuery}
                      onChange={(e) => setClassQuery(e.target.value)}
                    />
                    {classHits && classHits.items.length > 0 && (
                      <div className="mt-1 max-h-48 overflow-y-auto rounded-md border border-white-regular bg-white shadow-light">
                        {classHits.items.slice(0, 8).map((c) => (
                          <button
                            key={c.id}
                            type="button"
                            className="block w-full px-3 py-1.5 text-left text-sm transition-colors hover:bg-blue-100/40"
                            onClick={() => {
                              setClassId(c.id);
                              setClassLabel(
                                `${c.name} · ${c.start?.slice(0, 10) ?? ''}`,
                              );
                            }}
                          >
                            {c.name}
                            <span className="ml-2 text-xs text-gray-bold">
                              {c.start?.slice(0, 10)} ·{' '}
                              {c.instructorName ?? 'unassigned'}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
              <div>
                <div className={labelClass}>Instructor</div>
                <select
                  className={`${inputClass} w-full`}
                  value={pickInstructor}
                  onChange={(e) => setPickInstructor(e.target.value)}
                >
                  <option value="">— choose —</option>
                  {instructors.map((i) => (
                    <option key={i.name}>{i.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex flex-wrap items-end gap-3 md:col-span-2">
                <div>
                  <div className={labelClass}>Class rate</div>
                  <input
                    className={`${inputClass} w-28`}
                    type="number"
                    min="0"
                    value={classRate}
                    onChange={(e) => setClassRate(e.target.value)}
                  />
                </div>
                <div>
                  <div className={labelClass}>Prep rate</div>
                  <input
                    className={`${inputClass} w-28`}
                    type="number"
                    min="0"
                    value={prepRate}
                    onChange={(e) => setPrepRate(e.target.value)}
                  />
                </div>
                <div>
                  <div className={labelClass}>Contract</div>
                  <select
                    className={inputClass}
                    value={contractType}
                    onChange={(e) => setContractType(e.target.value)}
                  >
                    <option value="">—</option>
                    {['FTE', 'PTE', 'LLC'].map((c) => (
                      <option key={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <span className="pb-2 text-xs text-gray-bold">
                  Rates prefill from the instructor's latest assignment — check
                  them before saving.
                </span>
                <button
                  className={`${btnPrimary} ml-auto`}
                  disabled={!classId || !pickInstructor || create.isPending}
                  onClick={() =>
                    create.mutate({
                      classId,
                      instructorName: pickInstructor,
                      classRate:
                        classRate === '' ? undefined : Number(classRate),
                      prepRate: prepRate === '' ? undefined : Number(prepRate),
                      contractType: contractType || undefined,
                    })
                  }
                >
                  {create.isPending ? 'Assigning…' : 'Assign'}
                </button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner />
        </div>
      ) : (
        <PageBody wide>
          <Card>
            {data && data.items.length === 0 ? (
              <EmptyState
                title="No assignments match"
                hint="Try another instructor, or untick “Upcoming only”."
              />
            ) : (
              <div className="overflow-x-auto">
                <table className={`${tableClass} min-w-[900px]`}>
                  <thead className={theadClass}>
                    <tr>
                      <SortTh
                        k="start"
                        label="Start"
                        sort={sortKey}
                        dir={sortDir}
                        onSort={toggleSort}
                      />
                      <SortTh
                        k="course"
                        label="Course"
                        sort={sortKey}
                        dir={sortDir}
                        onSort={toggleSort}
                      />
                      <SortTh
                        k="instructor"
                        label="Instructor"
                        sort={sortKey}
                        dir={sortDir}
                        onSort={toggleSort}
                      />
                      <th className={thClass}>Location</th>
                      <th className={thClass}>Room</th>
                      <SortTh
                        k="type"
                        label="Type"
                        sort={sortKey}
                        dir={sortDir}
                        onSort={toggleSort}
                      />
                      <SortTh
                        k="status"
                        label="Status"
                        sort={sortKey}
                        dir={sortDir}
                        onSort={toggleSort}
                      />
                      <SortTh
                        k="contract"
                        label="Contract"
                        sort={sortKey}
                        dir={sortDir}
                        onSort={toggleSort}
                      />
                      <th className={thClass}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.items.map((a) => (
                      <tr
                        key={a.id}
                        className={a.classId ? rowClickClass : undefined}
                        onClick={() =>
                          a.classId &&
                          navigate(PATH.opsClassEdit.replace(':id', a.classId))
                        }
                      >
                        <td className={`${tdClass} whitespace-nowrap`}>
                          {a.startDate ?? '—'}
                        </td>
                        <td className={`${tdClass} font-medium text-gray-four`}>
                          {a.courseName ?? '—'}
                          {a.localOrigin && (
                            <span className="ml-2">
                              <OriginBadge local />
                            </span>
                          )}
                        </td>
                        <td className={`${tdClass} whitespace-nowrap`}>
                          {a.instructorName ?? '—'}
                        </td>
                        <td className={`${tdClass} max-w-[220px]`}>
                          {a.location === 'Onsite' ? (
                            <span title={a.locationAddress ?? ''}>
                              on site
                              {a.locationAddress
                                ? ` · ${a.locationAddress}`
                                : ''}
                            </span>
                          ) : (
                            a.location ?? '—'
                          )}
                        </td>
                        <td className={`${tdClass} whitespace-nowrap`}>
                          {a.room ?? '—'}
                        </td>
                        <td className={`${tdClass} whitespace-nowrap`}>
                          {a.eventType ?? '—'}
                        </td>
                        <td className={`${tdClass} whitespace-nowrap`}>
                          <Pill value={a.eventStatus} />
                        </td>
                        <td className={`${tdClass} whitespace-nowrap`}>
                          {a.contractType ?? '—'}
                        </td>
                        <td className={`${tdClass} text-right`}>
                          {a.localOrigin && (
                            <button
                              className={`${btnDanger} px-2 py-1 text-xs`}
                              onClick={(e) => {
                                e.stopPropagation();
                                remove.mutate(a.id);
                              }}
                            >
                              remove
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <Pagination
              page={page}
              pages={pages}
              total={data?.total}
              onPage={setPage}
            />
          </Card>
        </PageBody>
      )}
    </>
  );
};

export default OpsAssignments;
